import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Save, X, ChevronDown, Search, Users, Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import auditUserManagementService from '../../../../../../services/audit_services/auditUserManagementService';
import auditProgramService from '../../../../../../services/audit_services/auditProgramService';

export default function AuditProgramCreate() {
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    objective: '', // Changed from array to single field
    scope: [''], // Changed from single to array
    reference: '',
    approvers: [], // Changed to array for multiple selection
    parties: []
  });

  // User search for parties involved
  const [userSearch, setUserSearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  // User search for approvers
  const [approverSearch, setApproverSearch] = useState({
    query: '',
    results: [],
    showDropdown: false,
    loading: false
  });

  const [errors, setErrors] = useState({});

  // Available roles for parties
  const partyRoles = [
    { value: 'auditor', label: 'Auditor' },
    { value: 'auditee', label: 'Auditee' },
    { value: 'participant', label: 'Participant' },
    { value: 'approver', label: 'Approver' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle scope changes (now array)
  const handleScopeChange = (index, value) => {
    const newScope = [...formData.scope];
    newScope[index] = value;
    setFormData(prev => ({ ...prev, scope: newScope }));
  };

  const addScope = () => {
    setFormData(prev => ({
      ...prev,
      scope: [...prev.scope, '']
    }));
  };

  const removeScope = (index) => {
    if (formData.scope.length > 1) {
      const newScope = formData.scope.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, scope: newScope }));
    }
  };

  // Real user search function using the service
  const searchUsers = async (query) => {
    try {
      const result = await auditUserManagementService.filterAuditUserSearch(authTokens.access, query);
      if (result.success) {
        return result.data.results || result.data;
      } else {
        console.error('Error searching users:', result.error);
        return [];
      }
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  };

  // User search functionality for parties
  const handleUserSearch = async (query) => {
    setUserSearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const users = await searchUsers(query);
        setUserSearch(prev => ({
          ...prev,
          results: users,
          showDropdown: true,
          loading: false
        }));
      } catch (error) {
        console.error('Error searching users:', error);
        setUserSearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setUserSearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  // User search functionality for approvers
  const handleApproverSearch = async (query) => {
    setApproverSearch(prev => ({ ...prev, query, loading: true }));

    if (query.length >= 2) {
      try {
        const users = await searchUsers(query);
        setApproverSearch(prev => ({
          ...prev,
          results: users,
          showDropdown: true,
          loading: false
        }));
      } catch (error) {
        console.error('Error searching approvers:', error);
        setApproverSearch(prev => ({ ...prev, loading: false }));
      }
    } else {
      setApproverSearch(prev => ({
        ...prev,
        results: [],
        showDropdown: false,
        loading: false
      }));
    }
  };

  const addApprover = (user) => {
    // Check if user is already added as approver
    if (!formData.approvers.find(a => a.id === user.id)) {
      setFormData(prev => ({
        ...prev,
        approvers: [...prev.approvers, {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          role: user.role
        }]
      }));
    }
    
    // Clear search
    setApproverSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  const removeApprover = (userId) => {
    setFormData(prev => ({
      ...prev,
      approvers: prev.approvers.filter(a => a.id !== userId)
    }));
  };

  const addParty = (user) => {
    // Check if user is already added
    if (!formData.parties.find(p => p.id === user.id)) {
      setFormData(prev => ({
        ...prev,
        parties: [...prev.parties, {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          userRole: user.role, // User's system role
          partyRole: 'participant' // Default party role
        }]
      }));
    }
    
    // Clear search
    setUserSearch({
      query: '',
      results: [],
      showDropdown: false,
      loading: false
    });
  };

  const removeParty = (userId) => {
    setFormData(prev => ({
      ...prev,
      parties: prev.parties.filter(p => p.id !== userId)
    }));
  };

  const updatePartyRole = (userId, newRole) => {
    setFormData(prev => ({
      ...prev,
      parties: prev.parties.map(p => 
        p.id === userId ? { ...p, partyRole: newRole } : p
      )
    }));
  };

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userSearch.showDropdown && !event.target.closest('.user-search-container')) {
        setUserSearch(prev => ({ ...prev, showDropdown: false }));
      }
      if (approverSearch.showDropdown && !event.target.closest('.approver-search-container')) {
        setApproverSearch(prev => ({ ...prev, showDropdown: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userSearch.showDropdown, approverSearch.showDropdown]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.objective.trim()) {
      newErrors.objective = 'L\'objectif est requis';
    }

    if (formData.scope.some(s => !s.trim())) {
      newErrors.scope = 'Tous les éléments du périmètre doivent être remplis';
    }

    if (!formData.reference.trim()) {
      newErrors.reference = 'Les normes de référence sont requises';
    }

    if (formData.approvers.length === 0) {
      newErrors.approvers = 'Au moins un approbateur est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Veuillez corriger les erreurs avant de soumettre');
      return;
    }

    setLoading(true);

    try {
      // Prepare data for API
      const apiData = {
        title: formData.title,
        objective: formData.objective,
        scope: formData.scope.filter(item => item.trim() !== ''), // Remove empty scope items
        reference: formData.reference,
        approvers: formData.approvers.map(approver => approver.id),
        parties: formData.parties.map(party => ({
          id: party.id,
          partyRole: party.partyRole
        }))
      };

      const result = await auditProgramService.createAuditProgram(apiData, authTokens.access);
      
      if (result.success) {
        showSuccess('Programme d\'audit créé avec succès');
        navigate('/dashboard/audit/programs');
      } else {
        showError(result.error || 'Erreur lors de la création du programme d\'audit');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating audit program:', error);
      showError('Erreur lors de la création du programme d\'audit');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/dashboard/audit/programs"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Créer un programme d'audit</h1>
          <p className="text-sm text-gray-600 mt-1">Créer un nouveau programme d'audit selon les normes ISO 19011</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Informations du programme</h3>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Titre du programme <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ex: Programme d'audit 2025"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Objective (single field) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Objectif <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.objective}
                onChange={(e) => handleInputChange('objective', e.target.value)}
                rows={3}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.objective ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Décrivez l'objectif principal de ce programme d'audit..."
              />
              {errors.objective && <p className="text-red-500 text-sm mt-1">{errors.objective}</p>}
            </div>

            {/* Scope (multiple fields) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Périmètre <span className="text-red-500">*</span>
              </label>
              {formData.scope.map((scopeItem, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={scopeItem}
                    onChange={(e) => handleScopeChange(index, e.target.value)}
                    className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.scope ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={`Élément du périmètre ${index + 1}`}
                  />
                  {formData.scope.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeScope(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addScope}
                className="inline-flex items-center text-green-600 hover:text-green-700 text-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-1" />
                Ajouter un autre élément
              </button>
              {errors.scope && <p className="text-red-500 text-sm mt-1">{errors.scope}</p>}
            </div>

            {/* Reference Standards */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Normes de référence <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.reference ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="ex: ISO 19011, ISO 31000"
              />
              {errors.reference && <p className="text-red-500 text-sm mt-1">{errors.reference}</p>}
            </div>
          </div>
        </div>

        {/* Approvers Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Approbateurs</h3>
          
          {/* Approver Search */}
          <div className="approver-search-container relative mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ajouter des approbateurs <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={approverSearch.query}
                onChange={(e) => handleApproverSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Rechercher des utilisateurs à ajouter comme approbateurs..."
              />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            {/* Approver Search Dropdown */}
            {approverSearch.showDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {approverSearch.loading ? (
                  <div className="p-3 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                    <span className="ml-2">Recherche...</span>
                  </div>
                ) : approverSearch.results.length > 0 ? (
                  approverSearch.results.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => addApprover(user)}
                      disabled={formData.approvers.find(a => a.id === user.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                        formData.approvers.find(a => a.id === user.id) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {user.first_name[0]}{user.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{user.email} • {user.role}</p>
                        </div>
                      </div>
                    </button>
                  ))
                ) : approverSearch.query.length >= 2 ? (
                  <div className="p-3 text-center text-gray-500">Aucun utilisateur trouvé</div>
                ) : (
                  <div className="p-3 text-center text-gray-500">Tapez pour rechercher...</div>
                )}
              </div>
            )}
          </div>

          {/* Selected Approvers */}
          {formData.approvers.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Approbateurs sélectionnés ({formData.approvers.length})</p>
              <div className="space-y-2">
                {formData.approvers.map((approver) => (
                  <div key={approver.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-green-600">
                          {approver.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{approver.name}</p>
                        <p className="text-xs text-gray-500">{approver.email} • {approver.role}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeApprover(approver.id)}
                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {formData.approvers.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>Aucun approbateur ajouté</p>
              <p className="text-sm">Recherchez et ajoutez les personnes qui approuveront ce programme</p>
            </div>
          )}
          {errors.approvers && <p className="text-red-500 text-sm mt-1">{errors.approvers}</p>}
        </div>

        {/* Parties Involved */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Parties impliquées</h3>
          
          {/* User Search */}
          <div className="user-search-container relative mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ajouter des personnes au programme
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={userSearch.query}
                onChange={(e) => handleUserSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Rechercher par nom ou email..."
              />
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>

            {/* Search Dropdown */}
            {userSearch.showDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {userSearch.loading ? (
                  <div className="p-3 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
                    <span className="ml-2">Recherche...</span>
                  </div>
                ) : userSearch.results.length > 0 ? (
                  userSearch.results.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => addParty(user)}
                      disabled={formData.parties.find(p => p.id === user.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                        formData.parties.find(p => p.id === user.id) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {user.first_name[0]}{user.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-xs text-gray-500">{user.email} • {user.role}</p>
                        </div>
                      </div>
                    </button>
                  ))
                ) : userSearch.query.length >= 2 ? (
                  <div className="p-3 text-center text-gray-500">Aucun utilisateur trouvé</div>
                ) : (
                  <div className="p-3 text-center text-gray-500">Tapez pour rechercher...</div>
                )}
              </div>
            )}
          </div>

          {/* Selected Parties */}
          {formData.parties.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Personnes sélectionnées ({formData.parties.length})</p>
              <div className="space-y-3">
                {formData.parties.map((party) => (
                  <div key={party.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600">
                          {party.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{party.name}</p>
                        <p className="text-xs text-gray-500">{party.email} • {party.userRole}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-xs text-gray-600">Rôle:</label>
                        <select
                          value={party.partyRole}
                          onChange={(e) => updatePartyRole(party.id, e.target.value)}
                          className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          {partyRoles.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeParty(party.id)}
                      className="p-1 text-red-600 hover:bg-red-100 rounded ml-2"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {formData.parties.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>Aucune personne ajoutée</p>
              <p className="text-sm">Recherchez et ajoutez les personnes impliquées dans ce programme</p>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <Link
            to="/dashboard/audit/programs"
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Création...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Créer le programme
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}